import React, { useState } from "react";
import { Helmet } from "react-helmet";

import {
    Form,
    Group,
    Input,
    Select,
    Heading,
    LoadingIcon,
} from "@peracto/peracto-ui";
import { useConfig } from "@peracto/peracto-config";
import axios from "axios";

import * as yup from "yup";
import { toast } from "react-toastify";

import { CodeFormatter } from "components/CodeFormatter";
import { CopyButton } from "components/CopyButton";

const schema = yup.object().shape({
    country: yup.string().required(),
    repairtimeTypeId: yup.string().required(),
    repairTypeCategory: yup.string().required(),
    repairTreeNodeId: yup.string(),
    carTypeGroup: yup.string().required(),
});

export const GetRepairTimeSubnodesByGroupV3Form = () => {
    const config = useConfig();
    const API_URL = config.get("api");

    const [loading, setLoading] = useState(false);
    const [responseData, setResponseData] = useState("");
    const [authenticationData, setAuthenticationData] = useState("");

    const onSubmitForm = async (formData, actions) => {
        setLoading(true);

        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
        };

        try {
            const { data: authData } = await axios.get(
                `${API_URL}/test-harness/haynes-pro/get-authentication-vrid`,
                {
                    headers,
                }
            );

            const { data: haynesData } = await axios.post(
                `${API_URL}/test-harness/haynes-pro/get-repair-time-subnodes-by-group`,
                {
                    ...formData,
                    repairTreeNodeId: formData.repairTreeNodeId || "root",
                },
                {
                    headers,
                }
            );

            setAuthenticationData(JSON.stringify(authData));
            setResponseData(JSON.stringify(haynesData));
        } catch (e) {
            console.error(e);

            toast.error(
                e?.error?.body?.hasOwnProperty("hydra:description")
                    ? e.error.body["hydra:description"]
                    : "There was an error submitting this form."
            );
        } finally {
            setLoading(false);
            actions.setSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>
                    Haynes Get Repair Time Subnodes By Group: V3 | Haynes Pro |
                    Web Services | Peracto
                </title>
            </Helmet>
            <div className="form-container">
                <Heading name="Haynes Get Repair Time Subnodes By Group: V3" />

                <Form
                    autoComplete="on"
                    values={{
                        country: "en",
                        repairtimeTypeId: "",
                        repairTypeCategory: "CAR",
                        repairTreeNodeId: "",
                        carTypeGroup: "QUICKGUIDES",
                    }}
                    schema={schema}
                    showActionPanel={false}
                    onSubmit={onSubmitForm}
                >
                    <Group
                        key="haynes-get-repair-time-subnodes-by-group-v3"
                        id="haynes-get-repair-time-subnodes-by-group-v3"
                        name="Haynes Get Repair Time Subnodes By Group V3"
                    >
                        <Select
                            name="country"
                            label="Country"
                            placeholder="Please select..."
                            required={true}
                            options={[
                                {
                                    label: "United Kingdom",
                                    value: "en",
                                },
                                { label: "Ireland", value: "ie" },
                            ]}
                            testId="country"
                        />

                        <Input
                            name="repairtimeTypeId"
                            label="Repair Time Type ID"
                            testId="repairtimeTypeId"
                            required
                        />

                        <Select
                            name="repairTypeCategory"
                            label="Repair Type Category"
                            placeholder="Please select..."
                            required={true}
                            options={[
                                {
                                    label: "Car",
                                    value: "CAR",
                                },
                                { label: "Truck", value: "truck" },
                            ]}
                            testId="repairTypeCategory"
                        />

                        <Input
                            name="repairTreeNodeId"
                            label="Repair Tree Node ID"
                            testId="repairTreeNodeId"
                            help="If left blank, 'root' will be used"
                        />

                        <Select
                            name="carTypeGroup"
                            label="Car Type Group"
                            placeholder="Please select..."
                            required={true}
                            options={[
                                {
                                    label: "Engine",
                                    value: "ENGINE",
                                },
                                {
                                    label: "Transmission",
                                    value: "TRANSMISSION",
                                },
                                {
                                    label: "Steering",
                                    value: "STEERING",
                                },
                                {
                                    label: "Brakes",
                                    value: "BRAKES",
                                },
                                {
                                    label: "Exterior",
                                    value: "EXTERIOR",
                                },
                                {
                                    label: "Electronics",
                                    value: "ELECTRONICS",
                                },
                                {
                                    label: "Quickguides",
                                    value: "QUICKGUIDES",
                                },
                            ]}
                            testId="carTypeGroup"
                        />

                        <button
                            className="btn btn-primary"
                            type="submit"
                            data-testid="submit"
                        >
                            Submit
                        </button>
                    </Group>

                    {loading && (
                        <div className="flex justify-center items-center py-5">
                            <LoadingIcon />
                        </div>
                    )}

                    {authenticationData?.length > 0 && !loading && (
                        <Group
                            key="authentication-details"
                            id="authentication-details"
                            name="Authentication Details"
                        >
                            <div className="flex items-center justify-between mt-2 mb-3">
                                <h4 className="text-[18px] font-semibold mr-2 mb-0">
                                    Haynes Authentication Results
                                </h4>{" "}
                                <div className="flex flex-row">
                                    <CopyButton content={authenticationData} />
                                </div>
                            </div>

                            <div className="border rounded-[4px] p-2">
                                <CodeFormatter
                                    code={authenticationData}
                                    language="json"
                                />
                            </div>
                        </Group>
                    )}

                    {responseData?.length > 0 && !loading && (
                        <Group
                            key="response-details"
                            id="response-details"
                            name="Response Details"
                        >
                            <div className="flex items-center justify-between mt-2 mb-3">
                                <h4 className="text-[18px] font-semibold mr-2 mb-0">
                                    getRepairTimeSubnodesByGroupV3 Response
                                </h4>{" "}
                                <CopyButton content={responseData} />
                            </div>
                            <div className="border rounded-[4px] p-2">
                                <CodeFormatter
                                    code={responseData}
                                    language="json"
                                />
                            </div>
                        </Group>
                    )}
                </Form>
            </div>
        </>
    );
};

GetRepairTimeSubnodesByGroupV3Form.displayName =
    "GetRepairTimeSubnodesByGroupV3Form";
